import React, { PropsWithChildren, useCallback, useRef, useState } from 'react'
import TetherComponent from 'react-tether'

import { Direction, SlidingDrawer } from '../../components/SlidingDrawer'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { useIsMobile } from '../../utils/mediaQueries'
import * as S from './Menu.styles'
import { AttachmentDirection, MenuProps } from './Menu.types'
import { MenuItems } from './MenuItems'

const Menu = ({
  menuItems,
  isActive = true,
  attachmentDirection = AttachmentDirection.topLeft,
  onClickMenuOptions,
  rootRef,
  children,
  displayOnHover = false,
  tetherStyles,
}: PropsWithChildren<MenuProps>): JSX.Element => {
  const [showDrawer, setShowDrawer] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState(-1)
  const targetRef = useRef<HTMLDivElement | null>(null)

  useOnClickOutside(targetRef, () => {
    setShowDrawer(false)
    setShowDropdown(false)
  })

  const handleClickMenuOptions = (e: React.SyntheticEvent) => {
    e.stopPropagation()

    const toggleState = (prevState: boolean) => !prevState

    setShowDrawer(toggleState)
    setShowDropdown(toggleState)

    onClickMenuOptions?.()
  }

  const handleMouseEnter = useCallback(() => {
    if (displayOnHover) setShowDropdown(true)
  }, [displayOnHover])

  const handleMouseLeave = useCallback(() => {
    if (displayOnHover) setShowDropdown(false)
  }, [displayOnHover])

  const isMobile = useIsMobile()

  if (isMobile)
    return (
      <>
        <S.ChildrenButton
          isActive={isActive && showDrawer}
          iconWidth="24px"
          onClick={handleClickMenuOptions}
        >
          {children}
        </S.ChildrenButton>

        {isActive && showDrawer && (
          <div ref={targetRef}>
            <SlidingDrawer
              baseRef={rootRef}
              direction={Direction.Down}
              hideCloseButton
              onClose={() => setShowDrawer(false)}
            >
              <MenuItems menuItems={menuItems} />
            </SlidingDrawer>
          </div>
        )}
      </>
    )

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <TetherComponent
        attachment={attachmentDirection}
        constraints={[
          {
            to: 'scrollParent',
            attachment: 'element',
          },
        ]}
        style={tetherStyles}
        renderTarget={ref => (
          <div ref={ref as React.RefObject<HTMLDivElement>}>
            <S.ChildrenButton
              isActive={isActive && showDropdown}
              iconWidth="24px"
              onClick={handleClickMenuOptions}
            >
              {children}
            </S.ChildrenButton>
          </div>
        )}
        renderElement={ref =>
          isActive &&
          showDropdown && (
            <div ref={targetRef as unknown as React.RefObject<HTMLDivElement>}>
              <S.Dropdown ref={ref as React.RefObject<HTMLDivElement>}>
                {menuItems.map((item, index) => {
                  if (item.content)
                    return (
                      <S.DropdownLink
                        key={item.label}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(-1)}
                      >
                        <S.DropdownLinkText>{item.label}</S.DropdownLinkText>
                        <S.DropdownLinkIcon />
                        {hoveredIndex === index && (
                          <S.DropdownLinkContent>{item.content}</S.DropdownLinkContent>
                        )}
                      </S.DropdownLink>
                    )

                  return (
                    <S.DropdownLink
                      key={item.label}
                      onClick={() => !item.disabled && item.action?.()}
                      disabled={item.disabled}
                    >
                      {item.icon} <S.DropdownLinkText>{item.label}</S.DropdownLinkText>
                    </S.DropdownLink>
                  )
                })}
              </S.Dropdown>
            </div>
          )
        }
      />
    </div>
  )
}

export { Menu }
